import { inject, Injectable } from '@angular/core';
import { CardsStore } from '../../../cards/core/stores/CardsStore';
import { CartItemTypes } from '../enums/CartItemTypes';
import { ICardPaymentDialogData } from '../interfaces/ICardPaymentDialogData';
import { CartStore } from '../stores/CartStore';
import { PaySeparatelyStore } from '../stores/PaySeparatelyStore';

export interface ICardPaymentValidationResult {
  shouldShowDialog: boolean;
  dialogData?: ICardPaymentDialogData;
}

@Injectable({
  providedIn: 'root'
})
export class CardPaymentValidationService {

  private readonly _cartStore = inject(CartStore);
  private readonly _cardsStore = inject(CardsStore);
  private readonly _paySeparatelyStore = inject(PaySeparatelyStore);

  public validateCardPayment(cardId: number): ICardPaymentValidationResult {
    const cart = this._cartStore.cart();

    if (!cart) {
      return { shouldShowDialog: false };
    }

    const customerCards = cart.customerCards || [];

    if (customerCards.length === 0) {
      return { shouldShowDialog: false };
    }

    const card = customerCards.find(c => c.cardId === cardId);

    if (!card) {
      return { shouldShowDialog: false };
    }

    const cardType = this._cardsStore.allCardTypes().find(ct => ct.cardTypeId === card.cardTypeId);

    if (!cardType) {
      return { shouldShowDialog: false };
    }

    const alreadyCoveredAmount = this.getAlreadyCoveredAmount(card.cardId);
    const totalPayableAmount = this.getTotalPayableAmount();
    const voucherAmount = this.getVoucherAmount();

    const maxAmountToCoverByCards = Math.max(0, totalPayableAmount - voucherAmount);
    const remainingAmount = Math.max(0, maxAmountToCoverByCards - alreadyCoveredAmount);
    
    const cardRemainingAmount = this.calculateCardRemainingAmount(
      card, 
      remainingAmount, 
      totalPayableAmount
    );

    const currentBalance = card.currentBalance || 0;
    const cardLoadAmount = this.getCardLoadAmount(card.cardId);
    const cardBalance = currentBalance + cardLoadAmount;

    if (cardType.flagCashCard && !cardType.flagNotLoadable && cardRemainingAmount > cardBalance) {
      const minLoadAmount = cardType.minLoadAmount || 0;
      const requiredLoadAmount = cardRemainingAmount - cardBalance;
      const minLoadAmountAfterOffset = Math.max(0, minLoadAmount - cardLoadAmount);
      const adjustedRequiredLoadAmount = Math.max(
        requiredLoadAmount,
        minLoadAmountAfterOffset
      );

      const dialogData: ICardPaymentDialogData = {
        card,
        currentBalance: currentBalance,
        cardType,
        minLoadAmount: adjustedRequiredLoadAmount
      };

      return {
        shouldShowDialog: true,
        dialogData
      };
    }

    return { shouldShowDialog: false };
  }

  /** Calculates the sum of card load amounts in the cart. */
  private getCardLoadAmount(cardId: number): number {
    const cart = this._cartStore.cart();
    if (!cart) return 0;
    const loadItems = cart.items.filter(
      item => item.itemType === CartItemTypes.LoadCard && item.cardId === cardId
    );
    return loadItems.reduce((sum, item) => sum + Math.abs(item.totalPrice), 0);
  }

  /** Calculates the total amount already covered by all CashCineCard items, excluding the current card. */
  private getAlreadyCoveredAmount(excludeCardId?: number): number {
    const cart = this._cartStore.cart();
    if (!cart) return 0;
    
    const cashCineCardItems = cart.items.filter(
      item => item.itemType === CartItemTypes.CashCineCard &&
              (!excludeCardId || item.cardId !== excludeCardId)
    );
    
    return cashCineCardItems.reduce((sum, item) => sum + Math.abs(item.totalPrice), 0);
  }

  /** Calculates the total value of CashVoucher items (reduces amount to cover by cards). */
  private getVoucherAmount(): number {
    const cart = this._cartStore.cart();
    if (!cart) return 0;

    const voucherItems = cart.items.filter(
      item => item.itemType === CartItemTypes.CashVoucher
    );

    return voucherItems.reduce((sum, item) => sum + Math.abs(item.totalPrice ?? 0), 0);
  }

  /** Calculates the total amount of items that can be paid with cards (when pay-separately ON: only selected positions). */
  private getTotalPayableAmount(): number {
    if (this._paySeparatelyStore.isPaySeparatelyModeEnabled()) {
      return this._paySeparatelyStore.selectedTicketsTotal()
        + this._paySeparatelyStore.selectedProductsAndPackagesTotal();
    }

    const cart = this._cartStore.cart();
    
    if (!cart) return 0;

    const payableItems = cart.items.filter(
      item => item.itemType === CartItemTypes.Ticket ||
              item.itemType === CartItemTypes.Product ||
              item.itemType === CartItemTypes.Package
    );

    return payableItems.reduce((sum, item) => sum + Math.abs(item.totalPrice), 0);
  }

  /** Calculates how much of the remaining amount this card needs to cover. */
  private calculateCardRemainingAmount(
    card: any,
    remainingAmount: number,
    totalPayableAmount: number
  ): number {
    if (card.flagOnlyForTickets === true) {
      const totalTicketsAmount = this._paySeparatelyStore.isPaySeparatelyModeEnabled()
        ? this._paySeparatelyStore.selectedTicketsTotal()
        : this._cartStore.cart()?.items
            .filter(item => item.itemType === CartItemTypes.Ticket)
            .reduce((sum, item) => sum + Math.abs(item.totalPrice), 0) ?? 0;

      const ticketsRatio = totalPayableAmount > 0
        ? totalTicketsAmount / totalPayableAmount
        : 0;

      return remainingAmount * ticketsRatio;
    }

    return remainingAmount;
  }
}
